import { useState } from "react";
import { Plus, PlusCircle } from "lucide-react";
import { v4 } from "uuid";
import { UUID } from "@bitory-gmbh/nidavellir";
import { toast } from "sonner";
import t from "@/lang/lang";
import { Report } from "@/services/backend/htz/report/types/report";
import { useHtzReportAddAssessmentMutation } from "@/services/backend/htz/report/api/report-api";
import {
  useHtzStructuralAssessmentCatalogShowActiveCatalogQuery,
  useHtzStructuralAssessmentCatalogShowQuery,
} from "@/services/backend/htz/inspection/api/structural-assessment-catalog-api";
import {
  Item,
  itemsForComponentKind,
} from "@/services/backend/htz/inspection/types/structural-assessment-catalog";
import { AhuComponentSelect } from "@/services/backend/htz/ahu/components/ahu-component-select";
import { ResultDot } from "@/services/backend/htz/inspection/components/inspection-result";
import { Button } from "@/shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useHtzAirHandlingUnitDataShowQuery } from "@/services/backend/htz/ahu/api/ahu-data-api";

/**
 * ReportStructuralAssessmentAddDropdown adds a structural assessment for
 * one component of the report's air handling unit, either blank or
 * prefilled from the assessment catalog of the company.
 *
 * The component is picked first: the catalog entries offered depend on the
 * kind of the chosen component, and an assessment always belongs to one
 * component.
 */
export function ReportStructuralAssessmentAddDropdown({
  report,
  disabled,
}: {
  report: Report;
  disabled?: boolean;
}) {
  const [componentId, setComponentId] = useState<UUID>("");

  const { data: ahuData } = useHtzAirHandlingUnitDataShowQuery(
    { id: report.ahuDataId ?? "" },
    { skip: !report.ahuDataId },
  );

  const { data: activeCatalog } =
    useHtzStructuralAssessmentCatalogShowActiveCatalogQuery(
      { companyId: report.companyId },
      { skip: !report.companyId },
    );
  const { data: catalog } = useHtzStructuralAssessmentCatalogShowQuery(
    { id: activeCatalog?.catalogId ?? "" },
    { skip: !activeCatalog || activeCatalog.catalogId === null },
  );

  const [add, { isLoading, error: addError }] =
    useHtzReportAddAssessmentMutation();

  const component = ahuData?.components.find(
    (c) => c.componentId === componentId,
  );
  const catalogItems = catalog
    ? itemsForComponentKind(catalog, component?.componentKindId ?? "")
    : [];

  const addBlank = () => {
    if (isLoading || componentId === "") {
      return;
    }

    add({
      id: report.id,
      type: "structural",
      assessmentId: v4(),
      componentId,
    })
      .unwrap()
      .then(() => {
        toast.success(t("Beurteilung hinzugefügt"));
      })
      .catch(() => {
        // error is surfaced via RTKQueryErrorAlert; swallow the rejection
      });
  };

  // The catalog entry is part of the add request: the backend applies it
  // in the same transaction, so a failure cannot leave a blank assessment
  // behind.
  const addFromCatalog = (item: Item) => {
    if (isLoading || componentId === "") {
      return;
    }

    add({
      id: report.id,
      type: "structural",
      assessmentId: v4(),
      componentId,
      structural: {
        requirement: item.requirement,
        deficiency: item.deficiency,
        recommendation: item.recommendation,
        result: item.result,
      },
    })
      .unwrap()
      .then(() => {
        toast.success(t("Beurteilung aus Vorlage hinzugefügt"));
      })
      .catch(() => {
        // error is surfaced via RTKQueryErrorAlert; swallow the rejection
      });
  };

  if (!report.ahuId) {
    return null;
  }

  return (
    <div className="grid justify-items-end gap-2">
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button size="sm" disabled={disabled || isLoading}>
            <PlusCircle />
            {t("Beurteilung")}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuLabel>{t("Komponente")}</DropdownMenuLabel>
          <DropdownMenuItem
            className="focus:bg-transparent"
            onSelect={(e) => e.preventDefault()}
          >
            <AhuComponentSelect
              ahuDataId={report.ahuDataId!}
              componentId={componentId}
              onComponentIdChange={setComponentId}
              disabled={disabled}
              className="w-full"
            />
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>{t("Beurteilung hinzufügen")}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={addBlank} disabled={componentId === ""}>
            <Plus />
            <span>{t("Blanko")}</span>
          </DropdownMenuItem>
          {catalogItems.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>{t("Aus Vorlage:")}</DropdownMenuLabel>
              {catalogItems.map((item) => (
                <DropdownMenuItem
                  key={item.id}
                  onClick={() => addFromCatalog(item)}
                  disabled={componentId === ""}
                >
                  <Plus />
                  <span className="flex items-center gap-2">
                    <ResultDot result={item.result} showLabel={false} />
                    <span>{item.deficiency}</span>
                  </span>
                </DropdownMenuItem>
              ))}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <RTKQueryErrorAlert error={addError} />
    </div>
  );
}
